// games/shared/locales/en.ts -- English (fallback) dictionary for the arcade
// games. This file is bundled statically and defines the full key set; every
// other locale ships the same keys (a test enforces completeness) and is
// lazy-imported on boot. Multi-line values (controls/tips) are
// newline-separated lists. Brand names ("Trippi Drift") stay untranslated.
export const en = {
  // --- shared intro card ---------------------------------------------------
  "intro.controls": "Controls",
  "intro.tips": "Tips",
  "intro.start": "START",

  // --- per-game instructions (intro card copy) -----------------------------
  "ins.drift.tagline": "Drift real streets. Beat the clock.",
  "ins.drift.goal":
    "Race 3 laps around real city blocks and set the fastest time. " +
    "Top times go on the leaderboard under your 3-letter tag.",
  "ins.drift.controlsTouch":
    "Hold LEFT or RIGHT at the bottom to steer\n" +
    "Hold BRAKE to slow down\n" +
    "The car accelerates by itself\n" +
    "Tap the blue BOOST button for a burst of speed (hold up to 3)",
  "ins.drift.controlsKeys":
    "Arrows or A/D to steer\nSpace or Down to brake\nB or Up for boost (hold up to 3 charges)",
  "ins.drift.tips":
    "Start your turn early. The car slides, so set up before the corner.\n" +
    "A short brake before a tight corner beats a long slide into a wall.\n" +
    "Every completed lap refills you back up to 3 boosts, so spend them freely.\n" +
    "Stop on the glowing charge spot for 0.5s to top your boosts back up.",

  "ins.rally.tagline": "35 km of jumps, crests and courage.",
  "ins.rally.goal":
    "Drive the Myllypuro to Myyrmaki stage as fast as you can. " +
    "Beat the ghost car that drives at the legal speed limit.",
  "ins.rally.controlsTouch":
    "Tap left or right half of the screen to steer\n" +
    "Hold the bottom strip to brake\n" +
    "The car accelerates by itself\n" +
    "Tap the boost button for a burst of speed (3 per stage)",
  "ins.rally.controlsKeys":
    "Arrows or A/D to steer\nSpace or Down to brake\nB or Up for boost (3 charges)",
  "ins.rally.tips":
    "Brake before a crest, not after. Big jumps look cool but cost time.\n" +
    "The ghost never breaks the speed limit. Highways are where you drop it.\n" +
    "The camera zooms out at speed, so read the road far ahead.",

  "ins.heli.tagline": "Five cities. One chopper. Go!",
  "ins.heli.goal":
    "Fly to 5 Finnish cities in order, as fast as you can. " +
    "Every leg is timed, and the total time is your score.",
  "ins.heli.controlsTouch":
    "Tap left or right half of the screen to steer\n" +
    "Hold the bottom strip to airbrake\n" +
    "The heli flies forward by itself",
  "ins.heli.controlsKeys": "Arrows or A/D to steer\nSpace or Down to airbrake",
  "ins.heli.tips":
    "Follow the compass arrow and watch the distance count down.\n" +
    "Fly straight lines. Every wobble adds seconds to the leg.\n" +
    "Share your seed so a friend can race the exact same trip.",

  "ins.charge3d.tagline": "Plan smart. Charge fast. Reach Jyvaskyla.",
  "ins.charge3d.goal":
    "Road-trip an electric car to Jyvaskyla without running out of battery. " +
    "Fewer charging stops and less total time means a better score.",
  "ins.charge3d.controlsTouch":
    "Tap a charger card to pick your next stop\n" +
    "Compare kW, detour and amenities before you choose\n" +
    "Confirm and the car drives the leg for you",
  "ins.charge3d.controlsKeys": "Arrows to browse charger cards\nEnter or Space to pick a stop",
  "ins.charge3d.tips":
    "A fast charger with a short detour usually beats a closer slow one.\n" +
    "Do not top up to full every time. Charge just enough to make the next stop.\n" +
    "Skipping a stop is a gamble. Check the battery math first.",

  "ins.tag.tagline": "Your heli. Your car. Stay together.",
  "ins.tag.goal":
    "Fly a helicopter tied to a driving car by a stretchy band. " +
    "Fly through all the golden rings along the road before the drive ends.",
  "ins.tag.controlsTouch":
    "Tap left or right half of the screen to steer\n" +
    "Hold the bottom strip to hover in place\n" +
    "Tap Detach to roam free, Return to fly back on autopilot",
  "ins.tag.controlsKeys": "Arrows or A/D to steer\nSpace or Down to hover",
  "ins.tag.tips":
    "The band stretches to 600 m, so swing wide and look around.\n" +
    "Detach when you spot something cool, then hit Return to snap back.\n" +
    "Anchor to the demo drive, or to a real GPS car for a live chase.",

  // --- common HUD ------------------------------------------------------------
  "hud.loading": "Loading…",
  "hud.loadingStage": "Loading stage…",
  "hud.go": "GO!",
  "hud.paused": "Paused - tap to resume",
  "hud.lap": "Lap {lap}/{laps}",
  "hud.brake": "BRAKE",
  "hud.turbo": "BOOST",
  "hud.chargeSpot": "Charging...",
  "hud.airbrake": "AIRBRAKE",
  "hud.hover": "HOVER",
  "hud.highway": "HIGHWAY",
  "hud.gateMissed": "MISSED GATE - GO BACK",
  "hud.wrongWay": "WRONG WAY",
  "hud.parChip": "par {delta}",
  "hud.underPar": "UNDER PAR!",
  "hud.overPar": "OVER PAR",
  "hud.parTime": "Par {time}",
  "hud.parGhost": "par",
  "hud.newPb": "NEW PB!",
  "hud.pbLine": "Personal best: {time}",
  "hud.retry": "Retry",
  "hud.menu": "Menu",
  "hud.needsWebgl2": "This game needs a newer browser (WebGL2).",

  // --- leaderboard panel -----------------------------------------------------
  "lb.title": "Leaderboard",
  "lb.tagPrompt": "Pick a 3-letter tag for the leaderboard",
  "lb.save": "Save",
  "lb.submitting": "Submitting…",
  "lb.offline": "Leaderboard offline",
  "lb.rank": "Your rank {rank} of {total}",
  "lb.unverified": "Unverified run",
  // --- aggregate leaderboards (in-game Leaderboards tab) ----------------------
  "lb.hubTitle": "Leaderboards",
  "lb.tabPlayers": "Top Players",
  "lb.tabTracks": "Popular Tracks",
  "lb.searchPlayers": "Search tag",
  "lb.searchTracks": "Search tracks",
  "lb.colPoints": "Points",
  "lb.colTracks": "Tracks",
  "lb.colRaces": "Races",
  "lb.noPlayers": "No ranked players yet. Be the first.",
  "lb.noTracks": "No tracks found.",
  "lb.loading": "Loading…",

  // --- drift menu / lobby / multiplayer ---------------------------------------
  "menu.raceKerava": "Race Kerava",
  "menu.raceHere": "Race nearby",
  "menu.raceHereNote": "Requires location permissions",
  "menu.playFriends": "Play with friends",
  "menu.leaderboards": "Leaderboards",
  "menu.findingTrack": "Finding a track near you...",
  "menu.joiningRoom": "Joining room...",
  "menu.noLocalTrack": "No local track: {message}",
  "menu.noGeo": "This browser has no location support.",
  "menu.geoFail": "Could not get your location.",
  "lobby.roomCode": "Room code",
  "lobby.host": "host",
  "lobby.you": "you",
  "lobby.startRace": "Start race",
  "lobby.preparing": "Preparing...",
  "lobby.waitingHost": "Waiting for host...",
  "lobby.leave": "Leave",
  "mp.racing": "racing...",
  "mp.backToLobby": "Back to lobby",

  // --- rally -------------------------------------------------------------------
  "rally.stageComplete": "STAGE COMPLETE",
  "rally.ghostLine": "Speed-limit driver: {ghost} - you: {you}",
  "rally.halfwaySplit": "HALFWAY {delta}s vs par",

  // --- heli ----------------------------------------------------------------------
  "heli.dailyRoute": "Daily Route #{seed}",
  "heli.route": "Route #{seed}",
  "heli.cities": "{n} cities",
  "heli.routeComplete": "Route complete!",
  "heli.total": "TOTAL",
  "heli.underParDelta": "{delta} UNDER PAR!",
  "heli.overParDelta": "{delta} over par",
  "heli.newRoute": "New route",
  "heli.share": "Share this route",
  "heli.linkCopied": "Link copied!",

  // --- tag -------------------------------------------------------------------------
  "tag.modeGps": "Live GPS",
  "tag.modeDemo": "Demo drive",
  "tag.modeBridge": "Routemap link",
  "tag.routing": "Routing {route}...",
  "tag.findingCar": "Finding the car...",
  "tag.noCar": "Hmm, no car found",
  "tag.errorReload": "{message} Check your connection and reload.",
  "tag.errorDemo": "{message} You can still take flight along a demo drive!",
  "tag.flyDemo": "Fly the demo drive",
  "tag.flyLofoten": "Fly Lofoten instead",
  "tag.tethered": "Tethered",
  "tag.free": "Free",
  "tag.returnToCar": "Return to car",
  "tag.flyingBack": "Flying back...",
  "tag.milestone": "{m} m from the car!",
  "tag.climb": "Climb",
  "tag.descend": "Descend",
  "tag.rings": "Rings {n}/{total}",
  "tag.ringGot": "Ring {n}/{total}!",
  "tag.winTitle": "All rings collected!",
  "tag.endTitle": "End of the road!",
  "tag.endRings": "Rings: {n}/{total}",
  "tag.endTime": "Time: {time}",
  "tag.flyAgain": "Fly again",

  // --- charge3d (current strings; surface under parallel redesign) -------------------
  "c3.chooseStop": "CHOOSE YOUR NEXT STOP",
  "c3.kmLeft": "{km} km left",
  "c3.stopsOne": "{n} stop",
  "c3.stopsMany": "{n} stops",
  "c3.finish": "FINISH",
  "c3.detour": "+{km} km detour",
  "c3.driveMin": "{min} min drive",
  "c3.stopMin": "+{min} min stop",
  "c3.noMoreStops": "no more stops",
  "c3.wontMakeIt": "WON'T MAKE IT",
  "c3.risky": "RISKY",
  "c3.batteryStat": "battery {pct}%",
  "c3.how":
    "Pick your charging stops. Fast chargers save time, detours cost it. Don't get stranded.",
  "c3.destinationReached": "DESTINATION REACHED",
  "c3.par": "Par: {time}",
  "c3.streak": "DAY {n} STREAK",
  "c3.driveAgain": "DRIVE AGAIN",
  "c3.shareResult": "Share result",
  "c3.copied": "Copied!",
  "c3.stranded": "STRANDED",
  "c3.shortOf": "{km} km short of {name}",
  "c3.batteryPlan": "The battery didn't agree with that plan.",
  "c3.retry": "RETRY",
  "c3.chargeToast": "{name}: +{pct}% in {min} min",
  "c3.badgeNonstop": "NONSTOP",
  "c3.badgeEfficient": "EFFICIENT",

  // --- slots HUD -------------------------------------------------------------------
  "slots.lap": "Lap {lap}/{laps}",
  "slots.boost": "BOOST",
  "slots.throttle": "THROTTLE",
  "slots.deslot": "OFF THE SLOT!",
  "slots.warn": "SLOW DOWN!",
  "slots.paused": "PAUSED",
  "slots.retry": "Retry",
  "slots.menu": "Menu",
  "slots.leave": "Leave",
  "slots.loading": "Loading...",
  "slots.go": "GO!",
  "slots.racing": "Racing...",
  "slots.finished": "Finished",
  "slots.position": "Pos {pos}",

  // --- slots menu / lobby ----------------------------------------------------------
  "slots.menu.title": "Trippi Slots",
  "slots.menu.play": "Play",
  "slots.menu.mp": "Multiplayer",
  "slots.menu.host": "Host a room",
  "slots.menu.back": "Back",
  "slots.menu.joinCode": "Enter room code",
  "slots.menu.join": "Join",
  "slots.menu.connecting": "Connecting...",
  "slots.menu.waitingHost": "Waiting for the host...",
  "slots.menu.layout": "Track",
  "slots.lobby.roomCode": "ROOM CODE",
  "slots.lobby.start": "Start race",
  "slots.lobby.leave": "Leave",
  "slots.lobby.you": "you",
  "slots.lobby.host": "HOST",

  // --- boot/error cards (main.tsx) ----------------------------------------------------
  "err.loadGame": "Failed to load game.",
  "err.loadStage": "Failed to load stage.",
  "err.loadTrack": "Failed to load track.",
  "err.loadRoute": "Failed to load route.",
  "err.retry": "Retry",
  // --- i18n stragglers: tag HUD + charge duration units (localized) -------
  "tag.boost": "BOOST",
  "tag.autoFollow": "Auto-follow",
  "tag.loadingSat": "Loading satellite imagery...",
  "c3.durHM": "{h} h {m} min",
  "c3.durM": "{m} min",

  // --- drift menu (extends the existing menu.* drift keys) -----------------
  "menu.playNow": "Play now",
  "menu.singlePlayer": "Single player",
  "menu.browseTracks": "Browse tracks",
  "menu.randomTrack": "Random track",
  "menu.hostScreen": "Host big screen",
  "menu.comingSoon": "Coming soon",
  "menu.comingSoonBody": "This feature is not available yet. Check back later.",
  "menu.back": "Back",
  "menu.waitingApproval": "Waiting for the host to let you in...",
  "menu.noTracksTitle": "No suitable locations",
  "menu.noTracksBody":
    "No drivable race loop near you, even after searching wider. " +
    "Find a track on the map, or create your own.",
  "menu.findOnMap": "Find tracks on the map",
  "menu.createOwn": "Create your own",
  "create.title": "Create a track",
  "create.search": "Search place...",
  "create.go": "Go",
  "create.modePin": "Drop pin",
  "create.modeDraw": "Draw route",
  "create.pinHint": "Tap the map to drop a start point",
  "create.length": "Length",
  "create.short": "Short",
  "create.medium": "Medium",
  "create.long": "Long",
  "create.shape": "Shape",
  "create.loop": "Loop",
  "create.figure8": "Figure 8",
  "create.build": "Build track here",
  "create.drawHint": "Tap to add points, drag to move",
  "create.undo": "Undo",
  "create.clear": "Clear",
  "create.laps": "Laps",
  "create.race": "Race this loop",
  "create.maxPoints": "Up to 10 points",
  "create.notFound": "Place not found",
  "create.searchFail": "Search failed",
  "create.buildFail": "No drivable loop there - try another spot",
  "create.seamBent": "Close the loop on a straighter stretch",
  "create.tooShort": "That loop is too short",
  "menu.hostGame": "Host a game",
  "menu.gamesNearYou": "Games near you",
  "menu.lookingAround": "Looking around...",
  "menu.noGamesNearby": "No open games nearby. Use a code below.",
  "menu.watchOnly": "Watch only (spectator)",
  "menu.refresh": "Refresh",
  "menu.codePlaceholder": "CODE",
  "menu.join": "Join",
  "menu.searchingWider": "Searching a wider area...",
  "menu.randomIn": "Random track in {n}s",
  "menu.league": "Class",
  "menu.leagueAmateur": "Club",
  "menu.leaguePro": "Pro",
  "menu.leagueAmateurNote": "Steering help keeps you on the road.",
  "menu.leagueProNote": "No help. You drive every corner.",
  "menu.disconnected": "Disconnected.",
  "menu.disconnectedReason": "Disconnected ({reason}).",
  "menu.trackNotFound": "Track not found.",
  "menu.invalidCode": "Enter a valid 4-character code.",

  // --- drift lobby (extends lobby.*) ---------------------------------------
  "lobby.wantsToJoin": "Wants to join",
  "lobby.letIn": "Let in",
  "lobby.deny": "No",
  "lobby.friendTitle": "Friend",
  "lobby.addFriendTitle": "Add friend (auto-admit next time)",
  "lobby.addFriend": "+ friend",
  "lobby.spectator": "spectator",
  "lobby.laps": "Laps",
  "lobby.track": "Track",
  "lobby.trackNearby": "A loop near your location",
  "lobby.trackRandom": "A random world track",
  "lobby.lapsAuto": "Auto",

  // --- drift identity editor -----------------------------------------------
  "ident.playingAs": "Playing as",
  "ident.setTag": "Set your tag",
  "ident.title": "Your arcade ID",
  "ident.tagLabel": "Your tag",
  "ident.reserved": "{tag} is reserved -- pick another tag.",
  "ident.secretKey": "Secret key",
  "ident.show": "Show",
  "ident.hide": "Hide",
  "ident.keyHint": "Save the key somewhere -- it is the only way to restore your tag.",
  "ident.save": "Save",
  "ident.cancel": "Cancel",
  "ident.logout": "Log out",
  "ident.logoutTitle": "Log out?",
  "ident.logoutWarn": "Are you sure? You will need your secret key to log back in as {tag}.",
  "ident.taken": "That emoji + tag combo is taken -- try another.",
  "ident.netError": "Could not reach the server -- try again.",
  "ident.saving": "Saving...",
  "ident.restoreOpen": "I have a code",
  "ident.restoreTitle": "Restore your ID",
  "ident.restoreHint": "Type your secret key to get your tag back on this device.",
  "ident.restoreBtn": "Restore",
  "ident.badCode": "Unknown code -- check it and try again.",
  "ident.back": "Back",

  // --- drift browse map ----------------------------------------------------
  "browse.raceThis": "Race this",
  "browse.search": "Search place...",
  "browse.go": "Go",
  "browse.notFound": "Place not found",
  "browse.close": "Close",
  "browse.back": "Back",
  "browse.figure8": "figure-8",

  // --- drift difficulty tier ------------------------------------------------
  "drift.tier.easy": "Easy",
  "drift.tier.medium": "Medium",
  "drift.tier.hard": "Hard",

  // --- drift HUD extras ----------------------------------------------------
  "hud.howToPlay": "How to play?",
  "hud.shareTrack": "Share track",
  "hud.trackCreator": "Creator:",
  "hud.venuesHeading": "Real kart tracks near this track",
  "hud.venuesNote": "Distances are from the track, not from you.",
  "hud.venuesAttrib": "Venue data (c) OpenStreetMap contributors, ODbL",
  "hud.venueKind": "Kart track",
  "hud.leave": "Leave",
  "hud.leavingIn": "Leaving in {s}s",
  "hud.bail": "Bail",
  "hud.buildingsDemolished": "Buildings demolished: {n}",
  "hud.reverse": "REVERSE",
  "hud.accel": "ACCEL",
  "hud.mute": "Mute",
  "hud.unmute": "Unmute",

  // --- drift spectator / big-screen panel ----------------------------------
  "spec.waiting": "Waiting for players...",
  "spec.totalDemolished": "{n} buildings demolished",

  // --- minigolf (PuluGolf / Pub Crawl Golf) --------------------------------
  // The board is a real city and the vocabulary is golf's, so two words need
  // deciding per language rather than translating: what a HOLE is called (the
  // route from tee to cup, not the cup), and whether "par" is a loanword.
  // Where a language uses "par" as-is, leave it -- it is the scoring term, not
  // a description. Keys marked SHOUTING are drawn in a pixel display face and
  // must be written already uppercased; see the pulu chrome catalogue for why
  // toUpperCase() cannot do it.
  "mg.loading": "Laying out the course",
  "mg.courseDefault": "Course",
  /** Distance units, appended to a number. */
  "mg.km": "km",
  "mg.m": "m",
  /** Shot clock, seconds remaining. */
  "mg.clockS": "{n}s",

  // in-play card. SHOUTING, all of them.
  "mg.hole": "HOLE",
  "mg.par": "PAR",
  "mg.stroke": "STROKE",
  "mg.total": "TOTAL",
  "mg.ghostShort": "GHOST",
  "mg.toPin": "TO PIN",
  /** Level par, as one character on a scorecard. "E" for even. */
  "mg.even": "E",

  // how-to card
  "mg.how.heading": "How to play",
  "mg.how.s1h": "Aim and putt",
  "mg.how.s1p": "Drag back from the ball and let go. Buildings bounce you.",
  "mg.how.s2h": "Read the hole first",
  "mg.how.s2p": "Every hole opens looking straight down. Use it.",
  "mg.how.s3h": "The flying shot",
  "mg.how.s3p": "Hold full power, steady line. Clears the rooftops.",
  "mg.how.go": "Let's play",

  // start card
  "mg.tagline": "Putt through the streets. Bank off the buildings.",
  /** Lowercase, inside a hole-list row: "par 3 - 240 m". */
  "mg.parShort": "par",
  "mg.startRound": "Start round",
  "mg.coursePick": "{holes} holes",
  "mg.venuesHeading": "Real minigolf near this course",
  "mg.venuesNote": "Distances are from the course, not from you.",
  "mg.venuesAttrib": "Venue data (c) OpenStreetMap contributors, ODbL",
  "mg.venueKind": "Minigolf course",

  // survey
  "mg.startHole": "Start the hole",
  "mg.takeShot": "Take the shot",

  // aim hint. {left} {right} {space} are <kbd> glyphs the renderer supplies.
  "mg.hint.title": "Drag back from the ball, then let go",
  "mg.hint.body":
    "the further you drag, the harder you hit - buildings bounce you - keyboard:",
  "mg.hint.aim": "aim, hold",

  // the two special shots. SHOUTING on the band, sentence case underneath.
  "mg.chip.band": "CHIP: OFF THE ROOF, ANY POWER",
  "mg.chip.sub": "free - the height does the lifting",
  "mg.fly.ready": "FLYING SHOT READY - LET GO TO LAUNCH",
  "mg.fly.charging": "CHARGING FLYING SHOT {pct}%",
  "mg.fly.idle": "FLYING SHOT: HOLD FULL POWER, STEADY LINE, {s}s",
  "mg.fly.left": "{n} left this round - clears the buildings",

  // ball readouts. SHOUTING.
  "mg.lipOut": "TOO FAST FOR THE CUP",
  "mg.bounce1": "{n} bounce",
  "mg.bounceN": "{n} bounces",

  // on-board markers
  "mg.yourBall": "Your ball",
  "mg.ghost": "Ghost",
  /** SHOUTING: the label on the taxi, drawn over the board. */
  "mg.taxiLabel": "TAXI",
  "mg.portalTaxi": "Taxi",
  "mg.portalTaxiDrop": "Taxi drop",

  // hole out
  "mg.holeInOne": "Hole in one",
  "mg.underPar": "Under par",
  "mg.parWord": "Par",
  "mg.holeLine": "Hole {n} in {strokes}, par {par}",
  "mg.nextHole": "Next hole",
  "mg.learnMore": "Learn more",

  // round complete
  "mg.roundComplete": "Round complete",
  "mg.finalScore": "Final score",
  "mg.strokesUnit": "strokes",
  "mg.evenWord": "even",
  "mg.vsPar": "vs par",
  "mg.holesWord": "holes",
  "mg.ghostWord": "ghost",
  "mg.strokesRow": "{n} strokes",
  "mg.parRow": "par {par}",
  "mg.playAgain": "Play again",

  // toasts and fallbacks
  "mg.noWebgl": "WebGL2 required",
  "mg.thePin": "the pin",
  "mg.holeWord": "Hole",
  "mg.tee": "Tee",
  "mg.toast.powerCharged": "Power strike charged - let go to fly it",
  "mg.toast.oneHanded": "This one plays one-handed - pinch is optional",
  "mg.toast.powerStrike": "Power strike!",
  "mg.toast.chip": "Chip!",
  "mg.toast.holeInOne": "Hole in one!",
  "mg.toast.inTheHole": "In the hole",
  "mg.toast.outOfTime": "Out of time. +1",
  "mg.toast.offCart": "Off the cart!",
  "mg.toast.taxi": "Taxi!",
  "mg.toast.water": "In the water. +1",

  // weather, keyed by the API's labelCode rather than by its English word
  "mg.wx.fair": "Fair",
  "mg.wx.rain": "Rain",
  "mg.wx.snow": "Snow",
  "mg.wx.fog": "Fog",
  "mg.wx.windy": "Windy",
  "mg.wx.overcast": "Overcast",
  "mg.wx.cloudy": "Cloudy",
  "mg.wx.rainGusty": "Rain, gusty",
  "mg.wx.scorching": "Scorching",
} as const;

export type ArcadeMsgKey = keyof typeof en;
